import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { money, fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Repeat, Plus, Trash2, Play, Bell, Share2, Search } from "lucide-react";
import { toast } from "sonner";

const BLANK = { name: "", customer_name: "", customer_phone: "", frequency: "monthly", next_run: new Date().toISOString().slice(0, 10), items: [], active: true };

export default function Automation() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [prodQuery, setProdQuery] = useState("");

  const { data: dues } = useQuery({ queryKey: ["dues"], queryFn: async () => (await api.get("/reminders/dues")).data });
  const { data: recurring } = useQuery({ queryKey: ["recurring"], queryFn: async () => (await api.get("/recurring")).data });
  const { data: productData } = useQuery({ queryKey: ["auto-products"], queryFn: async () => (await api.get("/products", { params: { limit: 500 } })).data });
  const { data: settings } = useQuery({ queryKey: ["all-settings"], queryFn: async () => (await api.get("/settings")).data });
  const products = productData?.items || [];
  const filtered = prodQuery ? products.filter((p) => p.name.toLowerCase().includes(prodQuery.toLowerCase())).slice(0, 6) : [];

  function addItem(p) {
    setForm((f) => ({ ...f, items: [...f.items, { product_id: p.id, name: p.name, qty: 1, price: p.selling_price, discount: 0, tax_rate: p.tax_rate, unit: p.unit }] }));
    setProdQuery("");
  }
  function rmItem(i) { setForm((f) => ({ ...f, items: f.items.filter((_, x) => x !== i) })); }
  function updItem(i, k, v) { setForm((f) => ({ ...f, items: f.items.map((it, x) => x === i ? { ...it, [k]: v } : it) })); }

  async function save() {
    if (!form.items.length) { toast.error("Add at least one item"); return; }
    try {
      await api.post("/recurring", { ...form, items: form.items.map((it) => ({ ...it, qty: Number(it.qty), price: Number(it.price), tax_rate: Number(it.tax_rate) })) });
      toast.success("Recurring bill created");
      setOpen(false); setForm(BLANK); qc.invalidateQueries({ queryKey: ["recurring"] });
    } catch (e) { toast.error(apiError(e)); }
  }
  async function runNow(id) {
    try { const { data } = await api.post(`/recurring/${id}/run-now`); toast.success(`Bill ${data.bill_no} generated`); qc.invalidateQueries({ queryKey: ["dues"] }); }
    catch (e) { toast.error(apiError(e)); }
  }
  async function del(id) {
    try { await api.delete(`/recurring/${id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["recurring"] }); }
    catch (e) { toast.error(apiError(e)); }
  }
  function remind(d) {
    const biz = settings?.business || {};
    const num = (d.customer_phone || "").replace(/\D/g, "");
    const text = `*${biz.business_name || "BillOne"}*%0ADear ${d.customer_name || "Customer"}, this is a friendly reminder for your pending balance of ${money(d.outstanding)} on bill ${d.bill_no}.%0A%0AKindly clear at your earliest. Thank you!`;
    window.open(num ? `https://wa.me/${num.length === 10 ? "91" + num : num}?text=${text}` : `https://wa.me/?text=${text}`, "_blank");
  }

  return (
    <div className="space-y-5" data-testid="automation-page">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#0F172A]">Automation</h2>
        <p className="text-slate-500 text-sm">Recurring bills and payment reminders</p>
      </div>

      <Tabs defaultValue="reminders">
        <TabsList><TabsTrigger value="reminders" data-testid="tab-reminders"><Bell className="h-4 w-4 mr-1.5" />Payment Reminders</TabsTrigger><TabsTrigger value="recurring" data-testid="tab-recurring"><Repeat className="h-4 w-4 mr-1.5" />Recurring Bills</TabsTrigger></TabsList>

        <TabsContent value="reminders" className="mt-4">
          <Card className="border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-50 flex justify-between text-sm"><span className="font-semibold text-[#1A2A5E]">{dues?.count || 0} pending dues</span><span className="font-mono text-rose-600">{money(dues?.total_outstanding || 0)}</span></div>
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow><TableHead>Bill</TableHead><TableHead>Customer</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">Remind</TableHead></TableRow></TableHeader>
              <TableBody>
                {(dues?.dues || []).length ? dues.dues.map((d) => (
                  <TableRow key={d.id} data-testid={`due-row-${d.bill_no}`}>
                    <TableCell className="font-mono text-[#1A2A5E]">{d.bill_no}</TableCell>
                    <TableCell>{d.customer_name}<div className="text-xs text-slate-400">{d.customer_phone || "no phone"}</div></TableCell>
                    <TableCell className="text-slate-500 text-sm">{fmtDateTime(d.created_at)}</TableCell>
                    <TableCell className="text-right font-mono text-rose-600">{money(d.outstanding)}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => remind(d)} data-testid={`remind-${d.bill_no}`}><Share2 className="h-4 w-4 mr-1.5 text-emerald-600" />WhatsApp</Button></TableCell>
                  </TableRow>
                )) : <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">No pending dues 🎉</TableCell></TableRow>}
              </TableBody>
            </Table></div>
          </Card>
        </TabsContent>

        <TabsContent value="recurring" className="mt-4 space-y-4">
          <div className="flex justify-end"><Button onClick={() => setOpen(true)} className="bg-[#F4571E] hover:bg-[#D9420D]" data-testid="add-recurring"><Plus className="h-4 w-4 mr-1.5" />New Recurring Bill</Button></div>
          <Card className="border-slate-200 overflow-hidden"><div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Customer</TableHead><TableHead>Frequency</TableHead><TableHead>Next Run</TableHead><TableHead>Items</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {(recurring || []).length ? recurring.map((t) => (
                <TableRow key={t.id} data-testid={`recurring-row-${t.id}`}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell>{t.customer_name}</TableCell>
                  <TableCell><Badge variant="secondary" className="capitalize">{t.frequency}</Badge></TableCell>
                  <TableCell className="text-sm">{t.next_run}</TableCell>
                  <TableCell className="text-slate-500">{(t.items || []).length}</TableCell>
                  <TableCell className="text-right"><div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => runNow(t.id)} data-testid={`run-${t.id}`}><Play className="h-4 w-4 text-emerald-600" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(t.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div></TableCell>
                </TableRow>
              )) : <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400"><Repeat className="h-8 w-8 mx-auto mb-2 opacity-40" />No recurring bills yet</TableCell></TableRow>}
            </TableBody>
          </Table></div></Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#1A2A5E]">New Recurring Bill</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Template Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="recurring-name" /></div>
              <div><Label>Frequency</Label><Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent className="bg-white/95 backdrop-blur-md"><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent></Select></div>
              <div><Label>Customer Name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1" /></div>
              <div><Label>Customer Phone</Label><Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="mt-1" /></div>
              <div><Label>Start / Next Run</Label><Input type="date" value={form.next_run} onChange={(e) => setForm({ ...form, next_run: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={prodQuery} onChange={(e) => setProdQuery(e.target.value)} placeholder="Add product..." className="pl-9" data-testid="recurring-product-search" />
              {filtered.length > 0 && <div className="absolute z-10 mt-1 w-full bg-white rounded-lg border shadow-lg">{filtered.map((p) => <button key={p.id} onClick={() => addItem(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">{p.name}</button>)}</div>}
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_60px_80px_36px] gap-2 items-center bg-slate-50 rounded p-2">
                  <span className="text-sm truncate">{it.name}</span>
                  <Input type="number" value={it.qty} onChange={(e) => updItem(i, "qty", e.target.value)} className="h-8 text-center" />
                  <Input type="number" value={it.price} onChange={(e) => updItem(i, "price", e.target.value)} className="h-8 text-right" />
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => rmItem(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
            <Button onClick={save} className="w-full bg-[#1A2A5E]" data-testid="save-recurring">Create Recurring Bill</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
